import Container from './Container';
import Overlay from './Overlay';
import { MessageTypes } from '../types/MessageTypes';

class Survey {
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private resizeListener: (() => void) | null = null;
  private keydownListener: ((event: KeyboardEvent) => void) | null = null;
  private outsideClickListener: ((event: MouseEvent) => void) | null = null;
  private outsideCloseEnableTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastPositionData: {
    position: string;
    dimensions: { width: number; height: number };
    hasBackdrop: boolean;
    closeOnOutsideClick?: boolean;
    expandToViewport?: boolean;
  } | null = null;

  constructor() {
    this.startListeners();
  }

  private calculateIframePosition(
    position: string,
    dimensions: { width: number; height: number }
  ): { top: string; left: string } {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 20;

    let top: string;
    let left: string;

    switch (position) {
      case 'top-left':
        top = `${padding}px`;
        left = `${padding}px`;
        break;
      case 'top-right':
        top = `${padding}px`;
        left = `${viewportWidth - dimensions.width - padding}px`;
        break;
      case 'bottom-left':
        top = `${viewportHeight - dimensions.height - padding}px`;
        left = `${padding}px`;
        break;
      case 'bottom-right':
        top = `${viewportHeight - dimensions.height - padding}px`;
        left = `${viewportWidth - dimensions.width - padding}px`;
        break;
      case 'top-center':
        top = `${padding}px`;
        left = `${(viewportWidth - dimensions.width) / 2}px`;
        break;
      case 'bottom-center':
        top = `${viewportHeight - dimensions.height - padding}px`;
        left = `${(viewportWidth - dimensions.width) / 2}px`;
        break;
      case 'center':
        top = `${(viewportHeight - dimensions.height) / 2}px`;
        left = `${(viewportWidth - dimensions.width) / 2}px`;
        break;
      default:
        top = `${viewportHeight - dimensions.height - padding}px`;
        left = `${viewportWidth - dimensions.width - padding}px`;
    }

    return { top, left };
  }

  // Com uma lista de seleção aberta o iframe cobre a tela inteira: o popover só
  // pode ser desenhado dentro do iframe e precisa enxergar a tela toda para decidir
  // entre abrir para baixo ou para cima. O card continua no mesmo lugar porque, lá
  // dentro, é posicionado com as mesmas regras (positionClasses + 20px de margem).
  private applyPosition(data: {
    position: string;
    dimensions: { width: number; height: number };
    expandToViewport?: boolean;
  }) {
    if (data.expandToViewport) {
      Container.getInstance().resizeAndPosition(
        { top: '0px', left: '0px' },
        { width: `${window.innerWidth}px`, height: `${window.innerHeight}px` }
      );
      return;
    }

    Container.getInstance().resizeAndPosition(
      this.calculateIframePosition(data.position, data.dimensions),
      {
        width: `${data.dimensions.width}px`,
        height: `${data.dimensions.height}px`
      }
    );
  }

  private sendCloseSurvey = () => {
    Container.getInstance().sendMessage({
      type: MessageTypes.CLOSE_SURVEY
    });
  };

  // "Dentro da pesquisa" é o iframe e só ele. O conteúdo roda num iframe
  // cross-origin, então um clique lá dentro não vira evento nenhum neste
  // documento — o único nó do host que conta como "dentro" é o container.
  private isInsideWidget(target: EventTarget | null): boolean {
    const container = Container.getInstance().getContainer?.();
    return !!container && target instanceof Node && container.contains(target);
  }

  private syncOutsideClose(closeOnOutsideClick?: boolean) {
    this.clearKeydownListener();
    this.clearOutsideClickListener();
    if (this.outsideCloseEnableTimeout) {
      clearTimeout(this.outsideCloseEnableTimeout);
      this.outsideCloseEnableTimeout = null;
    }

    if (!closeOnOutsideClick) {
      return;
    }

    // Sempre limpa antes; só reativa depois de um tick para não consumir o clique
    // que acabou de abrir/posicionar a pesquisa (efeito "pisca e some").
    this.outsideCloseEnableTimeout = setTimeout(() => {
      this.outsideCloseEnableTimeout = null;

      // O ouvinte é do DOCUMENTO do host, não do backdrop do overlay. Pelo
      // backdrop, "fechar ao clicar fora" só funcionaria com o overlay visível
      // — e aí ligar a opção escureceria a página de quem configurou a pesquisa
      // SEM backdrop, além de engolir o primeiro clique do usuário na aplicação
      // do cliente. Pelo documento vale para os dois casos (o clique no
      // backdrop também passa por aqui) e nada é bloqueado.
      this.outsideClickListener = (event: MouseEvent) => {
        if (this.isInsideWidget(event.target)) {
          return;
        }
        this.sendCloseSurvey();
      };
      // Captura: uma página que chame stopPropagation no próprio clique não
      // impediria o fechamento. Sem preventDefault — o clique continua valendo
      // para a aplicação do cliente.
      document.addEventListener('click', this.outsideClickListener, true);

      this.keydownListener = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          this.sendCloseSurvey();
        }
      };
      window.addEventListener('keydown', this.keydownListener);
    }, 300);
  }

  private clearKeydownListener() {
    if (this.keydownListener) {
      window.removeEventListener('keydown', this.keydownListener);
      this.keydownListener = null;
    }
  }

  private clearOutsideClickListener() {
    if (this.outsideClickListener) {
      document.removeEventListener('click', this.outsideClickListener, true);
      this.outsideClickListener = null;
    }
  }

  private syncOverlay(hasBackdrop: boolean, closeOnOutsideClick: boolean) {
    // O overlay é a cortina VISUAL e nada mais: quem configurou a pesquisa sem
    // backdrop não pode ganhar uma página escurecida só por ligar o fechamento
    // por clique fora.
    if (hasBackdrop) {
      Overlay.getInstance().show();
    } else {
      Overlay.getInstance().hide();
    }
    this.syncOutsideClose(closeOnOutsideClick);
  }

  private handleResize = () => {
    if (this.lastPositionData) {
      this.applyPosition(this.lastPositionData);

      this.syncOverlay(
        this.lastPositionData.hasBackdrop,
        !!this.lastPositionData.closeOnOutsideClick
      );
    }
  };

  private startListeners() {
    Container.getInstance().sendMessage({
      type: MessageTypes.RUNNING_SURVEY
    });

    this.messageListener = async (event: MessageEvent) => {
      if (event.data.type === MessageTypes.RESIZE_AND_POSITION_IFRAME) {
        const { position, dimensions, hasBackdrop, closeOnOutsideClick, expandToViewport } =
          event.data;

        this.lastPositionData = {
          position,
          dimensions,
          hasBackdrop,
          closeOnOutsideClick: !!closeOnOutsideClick,
          expandToViewport: !!expandToViewport
        };

        this.applyPosition(this.lastPositionData);

        Container.getInstance().show();

        this.syncOverlay(!!hasBackdrop, !!closeOnOutsideClick);
      }
    };

    this.resizeListener = this.handleResize;

    window.addEventListener('message', this.messageListener);
    window.addEventListener('resize', this.resizeListener);
  }

  public destroy(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }

    if (this.outsideCloseEnableTimeout) {
      clearTimeout(this.outsideCloseEnableTimeout);
      this.outsideCloseEnableTimeout = null;
    }

    this.clearKeydownListener();
    this.clearOutsideClickListener();

    this.lastPositionData = null;
  }
}

export default Survey;
